/**
 * Typed auth API client.
 *
 * Thin wrappers around the shared axios instance that unwrap the server
 * envelope into domain TS types. Keeping the envelope-unwrapping logic in
 * one module (a) prevents every caller from re-learning the
 * `{ status, data, authorization, meta }` shape, and (b) gives the
 * `authSlice` thunks a single, typed call site per endpoint.
 *
 * None of the helpers touch the Redux store directly — they return plain
 * values and let the slice decide what to do. The one exception is
 * `login` / `refresh` / `heartbeat`: each writes the returned tokens into
 * the in-memory `tokenStore` before returning, because the axios request
 * interceptor needs the token available synchronously for follow-up
 * requests even before the reducer runs.
 *
 * SH-AUTH-005.
 */
import axios from 'axios';
import { clearTokens, setTokens } from '@dfq/shared/auth/tokenStore';
import type {
  ApiEnvelope,
  AuthUser,
  LoginRequest,
  LoginResult,
  RegisterRequest,
  RegisterOutcome,
  RegisterResult,
  TokenRefreshResult,
} from '@dfq/shared/types/auth';

import { apiClient, getApiClientType } from './client';

/**
 * Unwrap a success envelope into its `data` block. Throws an `AuthApiError`
 * if the server returned `status: "error"` — caller is expected to catch
 * and convert to the appropriate UI / slice error state.
 */
export class AuthApiError extends Error {
  public readonly code: string | undefined;

  public readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(params: { message: string; code?: string; fieldErrors?: Record<string, string[]> }) {
    super(params.message);
    this.name = 'AuthApiError';
    this.code = params.code;
    this.fieldErrors = params.fieldErrors;
  }
}

function unwrap<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.status !== 'success') {
    throw new AuthApiError({
      message: envelope.message,
      code: envelope.error_code ?? envelope.error?.code,
      fieldErrors: envelope.errors,
    });
  }
  // `data` can be absent when the endpoint attaches only `authorization`
  // (refresh / heartbeat). Callers of those endpoints do not use this
  // helper — they read `envelope.authorization` directly.
  return envelope.data ?? ({} as T);
}

/**
 * POST /auth/login. Discriminated result — MFA branch returns a challenge
 * token; success branch returns the user + authorization and side-effects
 * the tokens into the in-memory store.
 */
export async function login(body: LoginRequest): Promise<LoginResult> {
  // Translate the terser TS `client` field to the wire-level `client_type`
  // field the backend's LoginRequest expects. A caller that omits it signs in
  // as this app's own client (`'storefront'` until `configureApiClient` runs).
  const { client, ...rest } = body;
  const requestBody = {
    ...rest,
    client_type: client ?? getApiClientType(),
  };
  const response = await apiClient.post<
    ApiEnvelope<{
      user?: AuthUser;
      mfa_required?: boolean;
      challenge_token?: string;
    }>
  >('/auth/login', requestBody);
  const envelope = response.data;

  if (envelope.status !== 'success') {
    throw new AuthApiError({
      message: envelope.message,
      code: envelope.error_code ?? envelope.error?.code,
      fieldErrors: envelope.errors,
    });
  }

  const data = envelope.data ?? {};

  if (data.mfa_required === true) {
    return {
      mfaRequired: true,
      challengeToken: String(data.challenge_token ?? ''),
    };
  }

  if (!envelope.authorization || !data.user) {
    throw new AuthApiError({
      message: 'Login response missing authorization or user block',
    });
  }

  setTokens({
    accessToken: envelope.authorization.token,
    refreshToken: envelope.authorization.refresh_token,
    expiresIn: envelope.authorization.expires_in,
  });

  return {
    mfaRequired: false,
    user: data.user,
    authorization: envelope.authorization,
  };
}

export interface VerifyMfaChallengeRequest {
  challengeToken: string;
  /** 6-digit TOTP code. Send this or `recoveryCode`. */
  code?: string;
  recoveryCode?: string;
  /** Ask the server to skip the code step on this browser for 30 days. */
  rememberDevice?: boolean;
}

/**
 * POST /auth/mfa/verify. Trades the login challenge token plus a TOTP or
 * recovery code for the session a password login would have issued.
 */
export async function verifyMfaChallenge(
  body: VerifyMfaChallengeRequest,
): Promise<Extract<LoginResult, { mfaRequired: false }>> {
  const response = await apiClient.post<ApiEnvelope<{ user?: AuthUser }>>('/auth/mfa/verify', {
    challenge_token: body.challengeToken,
    code: body.code,
    recovery_code: body.recoveryCode,
    remember_device: body.rememberDevice,
  });
  const envelope = response.data;

  if (envelope.status !== 'success') {
    throw new AuthApiError({
      message: envelope.message,
      code: envelope.error_code ?? envelope.error?.code,
      fieldErrors: envelope.errors,
    });
  }

  if (!envelope.authorization || !envelope.data?.user) {
    throw new AuthApiError({
      message: 'Verification response missing authorization or user block',
    });
  }

  setTokens({
    accessToken: envelope.authorization.token,
    refreshToken: envelope.authorization.refresh_token,
    expiresIn: envelope.authorization.expires_in,
  });

  return {
    mfaRequired: false,
    user: envelope.data.user,
    authorization: envelope.authorization,
  };
}

/**
 * POST /auth/logout. Revokes the server-side session, clears this app's
 * refresh cookie, and clears the in-memory tokens. Tolerates missing tokens
 * locally so a stale tab hitting "sign out" never throws.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post<ApiEnvelope>('/auth/logout', { client_type: getApiClientType() });
  } finally {
    // Always clear locally even if the server call failed — the user's
    // intent was to sign out.
    clearTokens();
  }
}

/**
 * POST /auth/heartbeat. The SPA sliding-window keep-alive. Reads the
 * returned authorization block and replaces the tokens in the in-memory
 * store so the next outbound request carries the fresh bearer.
 */
export async function heartbeat(): Promise<TokenRefreshResult> {
  const response = await apiClient.post<ApiEnvelope<Record<string, never>>>('/auth/heartbeat');
  const envelope = response.data;

  if (envelope.status !== 'success') {
    throw new AuthApiError({
      message: envelope.message || 'Heartbeat failed',
      code: envelope.error_code ?? envelope.error?.code,
    });
  }

  // HeartbeatController nests the authorization payload under
  // `data.authorization`; the envelope-level `authorization` slot is only
  // populated when the controller opts in via the auth template. Accept
  // either location — prefer the envelope-level copy when present so any
  // template-driven enrichment wins, fall back to the data-nested copy
  // (the actual production shape today).
  const payload =
    envelope.authorization ??
    (envelope.data as unknown as { authorization?: typeof envelope.authorization })?.authorization;

  if (!payload) {
    throw new AuthApiError({ message: 'Heartbeat response missing tokens' });
  }

  setTokens({
    accessToken: payload.token,
    refreshToken: payload.refresh_token,
    expiresIn: payload.expires_in,
  });

  return { authorization: payload };
}

/**
 * POST /auth/refresh. Manually triggered refresh — the response interceptor
 * also calls this path, but exposing it as a public helper lets the
 * `authSlice` or a background task rotate tokens proactively.
 */
export async function refresh(): Promise<TokenRefreshResult> {
  // Explicitly use the refresh marker so the response interceptor does
  // not re-refresh this call if the refresh token itself is expired.
  const response = await apiClient.request<ApiEnvelope<Record<string, never>>>({
    method: 'POST',
    url: '/auth/refresh',
    data: { client_type: getApiClientType() },
    _dfqIsRefresh: true,
  } as Record<string, unknown>);

  const envelope = response.data;

  if (envelope.status !== 'success' || !envelope.authorization) {
    throw new AuthApiError({
      message: envelope.message || 'Refresh failed',
      code: envelope.error_code ?? envelope.error?.code,
    });
  }

  setTokens({
    accessToken: envelope.authorization.token,
    refreshToken: envelope.authorization.refresh_token,
    expiresIn: envelope.authorization.expires_in,
  });

  return { authorization: envelope.authorization };
}

/**
 * GET /auth/me. Returns the current user profile. Used for re-hydrating
 * after a hard reload when a valid session cookie / bearer is available,
 * and for RBAC-aware permission refresh.
 */
export async function me(): Promise<AuthUser> {
  const response = await apiClient.get<ApiEnvelope<{ user: AuthUser }>>('/auth/me');
  const envelope = response.data;
  const data = unwrap(envelope);
  if (!data.user) {
    throw new AuthApiError({ message: '/auth/me envelope missing user' });
  }
  return data.user;
}

/**
 * POST /auth/password/forgot. Request a password-reset email for the
 * given address.
 *
 * The platform always returns a 200 regardless of whether the address
 * belongs to a registered account — this is an intentional anti-enumeration
 * control (see `SF-AUTH-003` / Spec §5.3.9). Callers should therefore treat
 * every 2xx identically and render a neutral "check your inbox" confirmation.
 *
 * Some deployments may still surface a 404 for unknown addresses (e.g. when
 * a rate-limit layer short-circuits before the controller runs); the caller
 * (`ForgotPasswordView`) collapses that case into the same success path so
 * the UI never leaks account existence.
 *
 * No auth header is required — this endpoint is fully public.
 *
 * SH-AUTH-005 / SF-AUTH-003.
 */
export async function forgotPassword(email: string): Promise<void> {
  const response = await apiClient.post<ApiEnvelope<null>>('/auth/password/forgot', { email });
  const envelope = response.data;

  if (envelope.status !== 'success') {
    throw new AuthApiError({
      message: envelope.message,
      code: envelope.error_code ?? envelope.error?.code,
      fieldErrors: envelope.errors,
    });
  }
}

/**
 * POST /auth/password/reset. Completes a reset with the token from the emailed
 * link. Unlike the request endpoint this one reports failure — the caller is
 * holding a link and needs to be told when it has expired.
 */
export async function resetPassword(
  email: string,
  token: string,
  password: string,
): Promise<void> {
  let response;
  try {
    response = await apiClient.post<ApiEnvelope<null>>('/auth/password/reset', {
      email,
      token,
      password,
    });
  } catch (error) {
    // Axios rejects on 4xx before the envelope check below, so an expired
    // token would otherwise surface as "Request failed with status code 422".
    // The platform's message is the one worth showing.
    if (axios.isAxiosError(error)) {
      const envelope = error.response?.data as ApiEnvelope<null> | undefined;
      throw new AuthApiError({
        message: envelope?.message ?? 'That reset link is no longer valid. Request a new one.',
        code: envelope?.error_code ?? envelope?.error?.code,
        fieldErrors: envelope?.errors,
      });
    }
    throw error;
  }

  const envelope = response.data;

  if (envelope.status !== 'success') {
    throw new AuthApiError({
      message: envelope.message,
      code: envelope.error_code ?? envelope.error?.code,
      fieldErrors: envelope.errors,
    });
  }
}


/**
 * POST /auth/register. Creates a new customer account and returns the same
 * authorization envelope as `/auth/login` — access/refresh tokens plus the
 * fully-hydrated user profile. Side-effects the tokens into the in-memory
 * store before returning so the next outbound request carries the bearer.
 *
 * Server validation errors (422) and email-conflict errors (409) are
 * surfaced as an `AuthApiError` with the server-provided `error_code` and
 * `fieldErrors` preserved so the view can map them back onto the form.
 *
 * When the email belongs to a deleted customer the server answers 202 with
 * `data.restore_pending` and no tokens; that resolves to
 * `{ restorePending: true }` (#1828).
 *
 * SH-AUTH-005 / SF-AUTH-002.
 */
export async function register(body: RegisterRequest): Promise<RegisterOutcome> {
  const response = await apiClient.post<ApiEnvelope<{ user?: AuthUser; restore_pending?: boolean }>>(
    '/auth/register',
    body,
  );

  if (response.data.status === 'success' && response.data.data?.restore_pending === true) {
    return { restorePending: true };
  }

  return signedInResult(response.data, 'Register');
}

/**
 * POST /auth/restore-account. Redeems the emailed restore link for a deleted
 * customer, setting the account's password, and signs them in with the same
 * envelope as `/auth/register` (#1828).
 */
export async function restoreAccount(token: string, password: string): Promise<RegisterResult> {
  const response = await apiClient.post<ApiEnvelope<{ user?: AuthUser }>>('/auth/restore-account', {
    token,
    password,
  });

  return signedInResult(response.data, 'Restore');
}

function signedInResult(envelope: ApiEnvelope<{ user?: AuthUser }>, label: string): RegisterResult {
  if (envelope.status !== 'success') {
    throw new AuthApiError({
      message: envelope.message,
      code: envelope.error_code ?? envelope.error?.code,
      fieldErrors: envelope.errors,
    });
  }

  const data = envelope.data ?? {};

  if (!envelope.authorization || !data.user) {
    throw new AuthApiError({
      message: `${label} response missing authorization or user block`,
    });
  }

  setTokens({
    accessToken: envelope.authorization.token,
    refreshToken: envelope.authorization.refresh_token,
    expiresIn: envelope.authorization.expires_in,
  });

  return {
    user: data.user,
    authorization: envelope.authorization,
  };
}

/** Error codes the verify endpoint puts on a 422 (`error_code`). */
export type EmailVerificationFailure = 'verification_link_expired' | 'verification_link_invalid';

export interface EmailVerificationResult {
  verified: boolean;
  /** The address that is now verified (the new one after an email change). */
  email: string;
}

function authErrorFromAxios(error: unknown, fallbackMessage: string): unknown {
  if (axios.isAxiosError(error)) {
    const envelope = error.response?.data as ApiEnvelope<null> | undefined;
    return new AuthApiError({
      message: envelope?.message ?? fallbackMessage,
      code: envelope?.error_code ?? envelope?.error?.code,
      fieldErrors: envelope?.errors,
    });
  }
  return error;
}

/**
 * POST /auth/email/verify. Redeems the token from the emailed verification
 * link. Repeating a used link succeeds again. Failures carry the envelope's
 * `error_code` (`verification_link_expired` / `verification_link_invalid`) on
 * the thrown `AuthApiError.code`. Public endpoint; does not sign anyone in.
 *
 * PL-AUTH-126.
 */
export async function verifyEmail(token: string): Promise<EmailVerificationResult> {
  try {
    const response = await apiClient.post<
      ApiEnvelope<{ verification?: { verified?: boolean; email?: string } }>
    >('/auth/email/verify', { token });
    const verification = unwrap(response.data).verification;
    return { verified: verification?.verified === true, email: verification?.email ?? '' };
  } catch (error) {
    throw authErrorFromAxios(error, 'That verification link is not valid.');
  }
}

/**
 * POST /auth/email/resend. Asks for a fresh verification link. The platform
 * answers 200 whether or not the address needs one (anti-enumeration), so
 * callers show one neutral confirmation for every success.
 *
 * PL-AUTH-126.
 */
export async function resendEmailVerification(email: string): Promise<void> {
  try {
    const response = await apiClient.post<ApiEnvelope<null>>('/auth/email/resend', { email });
    unwrap(response.data);
  } catch (error) {
    throw authErrorFromAxios(error, 'Could not send the verification email.');
  }
}

/**
 * Public facade — mirrors how the other `@dfq/shared/api/*` modules
 * expose a single named object. Consumers can import named helpers OR
 * the bundled `authApi`.
 */
export const authApi = {
  login,
  logout,
  heartbeat,
  refresh,
  me,
  forgotPassword,
  resetPassword,
  register,
  restoreAccount,
  verifyEmail,
  resendEmailVerification,
} as const;
